import { loginRedirect } from "../lib/loginRedirect";
import { CityAutocomplete } from "../components/CityAutocomplete";
import type { UserResponseSettings } from "./ProfilePage";
import { ReferralAttribution } from "../components/ReferralAttribution";
import { readReferral, clearReferral } from "../lib/referrals";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiFetch, apiJson } from "../api/client";
import { useLocale } from "../contexts/LocaleContext";
import { useToast } from "../contexts/ToastContext";

// Profile-completion form shown when a logged-in user hasn't accepted the
// ToS yet (`data.tos === null` on the Google verify response). Mirrors the
// fields of the legacy register.html but as a controlled React form.

interface UserResponse {
  name?: string;
  tos?: string | null;
  settings?: UserResponseSettings;
  email?: string;
  phone_number?: string;
  [k: string]: unknown;
}

import { COUNTRY_CODES, type CountryCode } from "../lib/countries";

export function RegisterPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useLocale();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [land, setLand] = useState<CountryCode | "">("");
  const [city, setCity] = useState("");
  const [isArtist, setIsArtist] = useState(false);
  const [artistType, setArtistType] = useState("");
  const [otherArtistType, setOtherArtistType] = useState("");
  const [donationCert, setDonationCert] = useState(false);
  const [donationFirstName, setDonationFirstName] = useState("");
  const [donationLastName, setDonationLastName] = useState("");
  const [donationAddress, setDonationAddress] = useState("");
  const [donationPostalCode, setDonationPostalCode] = useState("");
  const [donationCity, setDonationCity] = useState("");
  const [donationCountry, setDonationCountry] = useState<CountryCode>("AT");
  const [birthdate, setBirthdate] = useState("");
  const [gender, setGender] = useState("");
  const [tos, setTos] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Prefill name/email from /api/v1/user (Google profile data the backend
  // already has). If not signed in, bounce to login.
  useEffect(() => {
    let cancelled = false;
    apiFetch("/user")
      .then(async (r) => {
        if (r.status === 401) {
          if (!cancelled) {
            if (!sessionStorage.getItem("postLoginRedirect")) sessionStorage.setItem("postLoginRedirect", "/register");
            navigate("/login", { replace: true, state: { reauthenticate: true } });
          }
          return null;
        }
        if (!r.ok) throw new Error("Profile could not be loaded");
        return (await r.json()) as UserResponse;
      })
      .then((data) => {
        if (!data || cancelled) return;
        if (data.tos) { navigate("/world", { replace: true }); return; }
        if (data.name) setName(String(data.name));
        if (data.email) setEmail(String(data.email));
        if (data.phone_number) setPhoneNumber(data.phone_number);
        const values = data.settings || {};
        setLand((values.state || "") as CountryCode | "");
        setCity(values.city || "");
        setIsArtist(values.isArtist ?? !!values.artistType);
        setArtistType(values.artistType || "");
        setOtherArtistType(values.otherArtistType || "");
        setDonationCert(values.donationCert ?? false);
        setDonationFirstName(values.donationFirstName || "");
        setDonationLastName(values.donationLastName || "");
        setDonationAddress(values.donationAddress || "");
        setDonationPostalCode(values.donationPostalCode || "");
        setDonationCity(values.donationCity || "");
        setDonationCountry((values.donationCountry || "AT") as CountryCode);
        setBirthdate(values.birthdate || "");
        setGender(values.gender || "");
        setLoaded(true);
      })
       .catch((err) => {
        console.error("Error fetching user data:", err);
        if (!cancelled) setLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!loaded || saving) return;
    if (!tos) {
      toast.show(t("register.tosRequired"), "danger");
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await apiJson("/user", {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          state: land,
          city,
          isArtist,
          artistType,
          otherArtistType,
          donationCert,
          donationFirstName,
          donationLastName,
          donationAddress,
          donationPostalCode,
          donationCity,
          donationCountry,
          birthdate,
          gender,
          tos,
          referral_code: readReferral(),
        }),
      });
      toast.show(t("register.success"), "success");
      clearReferral();
      const redirect = sessionStorage.getItem("postLoginRedirect");
      sessionStorage.removeItem("postLoginRedirect");
      const target = loginRedirect(redirect, "/world");
      navigate(target, { replace: true, state: { registrationComplete: true } });
    } catch (err) {
      console.error("Error during registration:", err);
      setSaveError(t("register.failed"));
      toast.show(t("register.failed"), "danger");
    } finally { setSaving(false); }
  };

  return (
    <section className="content registration-completion">
      <h1>{t("register.title")}</h1>
      <ReferralAttribution />
      <p className="centered">{t("register.completionHint")}</p>
      {loadFailed && <p role="alert">{t("register.loadFailed")}</p>}
      {saveError && <p role="alert">{saveError}</p>}

      <form className="custum-form" onSubmit={onSubmit}>
        <div>
          <label>
            {t("register.artistName")}
            <br />
            <input
              type="text"
              name="vorname"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={255}
            />
          </label>
        </div>

        <div>
          <label>
            {phoneNumber && !email ? t("phone.number") : t("register.email")}
            <br />
            <input
              type={phoneNumber && !email ? "tel" : "email"}
              name="email"
              value={phoneNumber && !email ? phoneNumber : email}
              onChange={(e) => setEmail(e.target.value)}
              required
              readOnly
            />
          </label>
        </div>

        <div>
          <label>
            {t("register.country")}
            <br />
            <select
              name="land"
              value={land}
              onChange={(e) => {
                setLand(e.target.value as CountryCode | "");
                setCity("");
              }}
              required
            >
              <option value="" disabled>
                {t("register.selectCountry")}
              </option>
              {land && !COUNTRY_CODES.includes(land as CountryCode) && <option value={land}>{land}</option>}
              {COUNTRY_CODES.map((countryCode) => (
                <option key={countryCode} value={countryCode}>
                  {t(`register.countries.${countryCode}`)}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div>
          <label>
            {t("register.city")}
            <br />
            <CityAutocomplete country={land} value={city} onChange={setCity} />
          </label>
        </div>

        <div className="block">
          <label>
            {t("register.isArtist")}
            <input
              type="checkbox"
              checked={isArtist}
              onChange={(e) => setIsArtist(e.target.checked)}
            />
          </label>

          {isArtist && (
            <div id="artistFields">
              <div>
                <label>
                  {t("register.artistType")}
                  <br />
                  <select
                    name="art"
                    value={artistType}
                    onChange={(e) => setArtistType(e.target.value)}
                  >
                    <option value="">{t("register.selectArtistType")}</option>
                    <option value="Maler">{t("register.painter")}</option>
                    <option value="Bildhauer">{t("register.sculptor")}</option>
                    <option value="Fotograf">{t("register.photographer")}</option>
                    <option value="Sonstiges">{t("register.other")}</option>
                  </select>
                </label>
              </div>
              {artistType === "Sonstiges" && (
                <div>
                  <label>
                    {t("register.otherSpecify")}
                    <br />
                    <input
                      type="text"
                      name="sonstiges"
                      value={otherArtistType}
                      onChange={(e) => setOtherArtistType(e.target.value)}
                    />
                  </label>
                </div>
              )}
            </div>
          )}

          <label>
            {t("register.donationCert")}
            <input
              type="checkbox"
              checked={donationCert}
              onChange={(e) => setDonationCert(e.target.checked)}
            />
          </label>

          {donationCert && (
            <div id="donationFields" style={{ display: "flex", flexWrap: "wrap" }}>
              <div>
                <label>
                  {t("register.firstName")}
                  <br />
                  <input
                    type="text"
                    value={donationFirstName}
                    onChange={(e) => setDonationFirstName(e.target.value)}
                  />
                </label>
              </div>
              <div>
                <label>
                  {t("register.lastName")}
                  <br />
                  <input
                    type="text"
                    value={donationLastName}
                    onChange={(e) => setDonationLastName(e.target.value)}
                  />
                </label>
              </div>
              <div>
                <label>
                  {t("register.address")}
                  <br />
                  <input
                    type="text"
                    value={donationAddress}
                    onChange={(e) => setDonationAddress(e.target.value)}
                  />
                </label>
              </div>
              <div>
                <label>
                  {t("register.postalCode")}
                  <br />
                  <input
                    type="text"
                    value={donationPostalCode}
                    onChange={(e) => setDonationPostalCode(e.target.value)}
                  />
                </label>
              </div>
              <div>
                <label>
                  {t("register.city")}
                  <br />
                  <input
                    type="text"
                    value={donationCity}
                    onChange={(e) => setDonationCity(e.target.value)}
                  />
                </label>
              </div>
              <div>
                <label>
                  {t("register.country")}
                  <br />
                  <select
                    value={donationCountry}
                    onChange={(e) =>
                      setDonationCountry(e.target.value as CountryCode)
                    }
                  >
                    {COUNTRY_CODES.map((countryCode) => (
                      <option key={countryCode} value={countryCode}>
                        {t(`register.countries.${countryCode}`)}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
          )}
        </div>

        <div>
          <label>
            {t("register.birthdate")}
            <br />
            <input
              type="date"
              value={birthdate}
              onChange={(e) => setBirthdate(e.target.value)}
            />
          </label>
        </div>

        <div>
          <label>
            {t("register.gender")}
            <br />
            <select value={gender} onChange={(e) => setGender(e.target.value)}>
              <option value="">{t("register.genderNone")}</option>
              <option value="m">{t("register.genderMale")}</option>
              <option value="w">{t("register.genderFemale")}</option>
              <option value="d">{t("register.genderDiverse")}</option>
            </select>
          </label>
        </div>

        <label>
          {t("register.tos").split(/\b(?:AGB|Terms & Conditions)\b/)[0]}
          <Link to="/agb">{t("bottomline.agb")}</Link>
          {t("register.tos").split(/\b(?:AGB|Terms & Conditions)\b/)[1]}
          <input
            type="checkbox"
            checked={tos}
            onChange={(e) => setTos(e.target.checked)}
          />
        </label>

        <div style={{ gridColumn: "span 2" }}>
          <button type="submit" disabled={!loaded || saving}>{t(saving ? "register.saving" : "register.submit")}</button>
        </div>
      </form>
    </section>
  );
}
